import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useUser } from "./Context/UserContext";
import Icon from "./ui/Icon";
import ProgressRing from "./ui/ProgressRing";
import EmptyState from "./ui/EmptyState";
import useCountUp from "./ui/useCountUp";
import { entrance } from "../lib/motion";
import "../styles/skillHome.css";
import api from "./axios";

const LEVEL_CLASS = {
    Beginner: "beginner",
    Intermediate: "intermediate",
    Expert: "expert",
};

const LEVEL_PROF = {
    Beginner: "prof-1",
    Intermediate: "prof-2",
    Expert: "prof-3",
};

export default function UserSkillDashboard() {
    const { empId, userInfo } = useUser();
    const [allSkills, setAllSkills] = useState([]);
    const [managerReview, setManagerReview] = useState(null);
    const [skillsLoading, setSkillsLoading] = useState(true);
    const history = useHistory();
    const pageRef = useRef(null);

    // Signature GSAP entrance: header → KPI tiles cascade → content cards.
    // Transform/opacity only; killed on unmount; no-op under reduced motion.
    useEffect(() => {
        const root = pageRef.current;
        if (!root) return undefined;
        const tl = entrance([
            { targets: root.querySelector(".page-head"), y: 14, at: 0 },
            { targets: root.querySelectorAll(".kpi-card"), y: 20, stagger: 0.09, at: 0.1 },
            { targets: [root.querySelector(".review-card"), root.querySelector(".skills-card")], y: 22, stagger: 0.12, at: 0.3 },
        ]);
        return () => {
            if (tl) tl.kill();
        };
    }, []);

    useEffect(() => {
        const fetchSkills = async () => {
            if (!empId) return;
            try {
                const res = await api.get(`/api/EmployeeSkill/myskills/${empId}`);
                setAllSkills(res.data || []);
            } catch (error) {
                //if (process.env.NODE_ENV !== "production") {
                //    console.error("Error fetching bench report:", error);
                //}
                //setAllSkills([]);
            } finally {
                setSkillsLoading(false);
            }
        };
        fetchSkills();
    }, [empId]);

    const levelPoints = { Beginner: 1, Intermediate: 2, Expert: 3 };

    const totalPoints = allSkills.reduce(
        (sum, s) => sum + (levelPoints[s.level] || 0),
        0
    );

    const skillsAcquired = managerReview
        ? [
            managerReview.managerPrimarySkill1,
            managerReview.managerPrimarySkill2,
            managerReview.managerPrimarySkill3,
            managerReview.managerSecondarySkill1,
            managerReview.managerSecondarySkill2,
            managerReview.managerSecondarySkill3
        ].filter(skill => skill && skill.trim() !== "").length
        : 0;

    const skillProgressPercent = Math.round(
        allSkills.reduce((sum, s) => {
            const val = s.level === "Expert" ? 100 : s.level === "Intermediate" ? 66 : 33;
            return sum + val;
        }, 0) / (allSkills.length || 1)
    );

    useEffect(() => {
        const fetchManagerReview = async () => {
            if (!empId) return;

            try {
                const res = await api.get(`/api/EmployeeSkill/managerreview/${empId}`);
                setManagerReview(res.data);
            } catch (error) {
                if (process.env.NODE_ENV !== "production") {
                    console.error("Error fetching manager review:", error);
                }
            }
        };

        fetchManagerReview();
    }, [empId]);

    const handleEdit = (employeeId) => {
        history.push(`/employee/${employeeId}`);
    };

    const animatedPoints = useCountUp(totalPoints);
    const animatedSkills = useCountUp(skillsAcquired);
    const animatedProgress = useCountUp(skillProgressPercent);

    const reviewedSkills = managerReview
        ? [
            { skill: managerReview.primarySkill1, review: managerReview.managerPrimarySkill1, type: "Primary" },
            { skill: managerReview.primarySkill2, review: managerReview.managerPrimarySkill2, type: "Primary" },
            { skill: managerReview.primarySkill3, review: managerReview.managerPrimarySkill3, type: "Primary" },
            { skill: managerReview.secondarySkill1, review: managerReview.managerSecondarySkill1, type: "Secondary" },
            { skill: managerReview.secondarySkill2, review: managerReview.managerSecondarySkill2, type: "Secondary" },
            { skill: managerReview.secondarySkill3, review: managerReview.managerSecondarySkill3, type: "Secondary" },
        ].filter(item => item.review)
        : [];

    const targetSkills = managerReview
        ? [
            { skill: managerReview.primarySkill1, target: managerReview.targetPrimarySkill1, type: "Primary" },
            { skill: managerReview.primarySkill2, target: managerReview.targetPrimarySkill2, type: "Primary" },
            { skill: managerReview.primarySkill3, target: managerReview.targetPrimarySkill3, type: "Primary" },
            { skill: managerReview.secondarySkill1, target: managerReview.targetSecondarySkill1, type: "Secondary" },
            { skill: managerReview.secondarySkill2, target: managerReview.targetSecondarySkill2, type: "Secondary" },
            { skill: managerReview.secondarySkill3, target: managerReview.targetSecondarySkill3, type: "Secondary" },
        ].filter(item => item.target)
        : [];

    return (
        <div className="skill-home-wrapper" ref={pageRef}>
            <header className="page-head">
                <span className="page-eyebrow">Dashboard</span>
                <h1 className="page-title-mt">Welcome back, {userInfo?.name}</h1>
                <p className="page-sub">
                    Here's where your skills and reviews stand today.
                </p>
            </header>

            {/* KPI TILES */}
            <div className="kpi-grid">
                <div className="card-mt card-pad kpi-card">
                    <div className="kpi-icon"><Icon name="star" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Total Points</h6>
                        <h3 className="kpi-value num">{animatedPoints}</h3>
                    </div>
                </div>

                <div className="card-mt card-pad kpi-card">
                    <div className="kpi-icon"><Icon name="check" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Current Approved Skills</h6>
                        <h3 className="kpi-value num">{animatedSkills}</h3>
                    </div>
                </div>

                <div className="card-mt card-pad kpi-card">
                    <div className="kpi-ring">
                        <ProgressRing value={skillProgressPercent} size={56} stroke={6} />
                        <span className="kpi-ring-value num">{animatedProgress}%</span>
                    </div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Skill Progress</h6>
                        <h3 className="kpi-value num">{animatedProgress}%</h3>
                    </div>
                </div>

                <div className="card-mt card-pad kpi-card">
                    <div className="kpi-icon kpi-icon-gold"><Icon name="graduation" size={19} /></div>
                    <div className="kpi-body">
                        <h6 className="kpi-label">Recommended Certifications</h6>
                        <h3 className="kpi-value kpi-value-sm">
                            {managerReview?.recommendedTraining
                                ? `${managerReview.recommendedTraining}${managerReview?.trainingDuration
                                    ? ` (${managerReview.trainingDuration} Months)`
                                    : ""
                                }`
                                : "Not Suggested"}
                        </h3>
                    </div>
                </div>
            </div>

            {/* MANAGER REVIEW SUMMARY */}
            <section className="card-mt card-pad review-card">
                <div className="review-card-head">
                    <h5 className="card-title-mt">
                        <span className="card-title-icon"><Icon name="clipboard" size={16} /></span>
                        Manager Review Summary
                    </h5>
                    {managerReview?.employeeId && (
                        <button
                            type="button"
                            className="btn btn-ghost-mt btn-sm view-details-link"
                            onClick={() => handleEdit(managerReview.employeeId)}
                        >
                            View full details
                            <Icon name="chevronRight" size={15} />
                        </button>
                    )}
                </div>

                {managerReview ? (
                    <>
                        <div className="review-columns">
                            {/* LEFT - Reviewed Skills */}
                            <div className="review-col">
                                <h6 className="section-title">Reviewed Skills</h6>
                                {reviewedSkills.map((item, idx) => (
                                    <div key={idx} className={`skill-box ${item.review.toLowerCase()}`}>
                                        <div className="skill-box-main">
                                            <p className="skill-name">{item.skill || "Unnamed Skill"}</p>
                                            <span className="skill-type">{item.type} Skill</span>
                                        </div>
                                        <span className={`badge-mt badge-${LEVEL_CLASS[item.review] || "neutral"}`}>
                                            {item.review}
                                        </span>
                                    </div>
                                ))}
                                {reviewedSkills.length === 0 && (
                                    <p className="no-review-text">No skills reviewed yet</p>
                                )}
                            </div>

                            {/* RIGHT - Target Competency Levels */}
                            <div className="review-col">
                                <h6 className="section-title">Target Competency Levels</h6>
                                {targetSkills.map((item, idx) => (
                                    <div key={idx} className={`skill-box ${item.target.toLowerCase()}`}>
                                        <div className="skill-box-main">
                                            <p className="skill-name">{item.skill || "Unnamed Skill"}</p>
                                            <span className="skill-type">{item.type} Skill</span>
                                        </div>
                                        <span className={`badge-mt badge-${LEVEL_CLASS[item.target] || "neutral"}`}>
                                            <Icon name="target" size={12} />
                                            {item.target}
                                        </span>
                                    </div>
                                ))}
                                {targetSkills.length === 0 && (
                                    <p className="no-review-text">No target levels set yet</p>
                                )}
                            </div>
                        </div>

                        {/* Training & Review Details */}
                        <h6 className="section-title training-title">Training &amp; Review Details</h6>
                        <div className="training-review-grid">
                            <p>
                                <strong>Recommended Certification</strong>
                                <span>{managerReview.recommendedTraining || "Not Suggested"}</span>
                            </p>
                            <p>
                                <strong>Duration</strong>
                                <span>
                                    {managerReview.trainingDuration
                                        ? managerReview.trainingDuration + " Months"
                                        : "Not Specified"}
                                </span>
                            </p>
                            <p>
                                <strong>Reviewed By</strong>
                                <span>{managerReview.reviewedBy || "N/A"}</span>
                            </p>
                            <p>
                                <strong>Reviewed On</strong>
                                <span className="num">
                                    {managerReview.reviewedOn
                                        ? new Date(managerReview.reviewedOn).toLocaleDateString()
                                        : "N/A"}
                                </span>
                            </p>
                        </div>
                    </>
                ) : (
                    <EmptyState
                        icon="clipboard"
                        title="No manager review yet"
                        text="Once your reporting manager completes a review, it will appear here."
                    />
                )}
            </section>

            {/* MY SKILLS */}
            <section className="card-mt card-pad skills-card">
                <h5 className="card-title-mt">
                    <span className="card-title-icon"><Icon name="layers" size={16} /></span>
                    My Skills
                </h5>

                {skillsLoading ? (
                    <div className="skills-skeleton" aria-hidden="true">
                        <div className="skeleton" style={{ height: 18, width: "40%" }} />
                        <div className="skeleton" style={{ height: 8 }} />
                        <div className="skeleton" style={{ height: 18, width: "55%" }} />
                        <div className="skeleton" style={{ height: 8 }} />
                        <div className="skeleton" style={{ height: 18, width: "35%" }} />
                        <div className="skeleton" style={{ height: 8 }} />
                    </div>
                ) : Array.isArray(allSkills) && allSkills.length > 0 ? (
                    allSkills.map((s, idx) => (
                        <div key={idx} className="skill-bar">
                            <div className="skill-label">
                                <span className="skill-label-name">{s.skill}</span>
                                <span className="skill-label-meta">
                                    <span className={`badge-mt badge-${LEVEL_CLASS[s.level] || "neutral"}`}>
                                        {s.level}
                                    </span>
                                    <span className="skill-points num">
                                        {levelPoints[s.level] || 0} pts
                                    </span>
                                </span>
                            </div>
                            <div className="meter">
                                <div
                                    className={`meter-fill ${LEVEL_PROF[s.level] || "prof-1"}`}
                                    style={{ width: `${((levelPoints[s.level] || 0) / 3) * 100}%` }}
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <EmptyState
                        icon="layers"
                        title="No skills recorded"
                        text="Submit your skill details from the Skill Performance Dashboard to see them here."
                    />
                )}
            </section>
        </div>
    );
}
